/**
 * @file schemas/staff.ts
 * @description 員工帳號管理 Zod 校驗 schema（供前端與後端共用）。
 *
 * 動態 RBAC（Task 12）：roles / primaryRole 改為 z.string().min(1) 放寬校驗，
 * 支援自定義角色代碼（如 cert_assistant）。
 * 後端在路由層額外校驗每個 code 是否存在於 roles 表且 status='active'。
 */
import { z } from 'zod';
import { DEFAULT_INITIAL_PASSWORD, adminAssignedPasswordSchema } from './password';
/**
 * 建立員工帳號請求 schema。
 * initialPassword 由管理員設定，首次登入後可自行修改。
 * 序51（2026-09-25）：省略時預設為系統初始密碼 1234567890；
 * 自訂時須符合 6–10 字元英數混合規則（或等於預設初始密碼）。
 */
export const createStaffSchema = z.object({
    username: z.string().min(3).max(50),
    name: z.string().min(1).max(100),
    email: z.string().email(),
    phone: z.string().max(20).nullish(),
    /**
     * 角色能力清單（多角色）。使用者可同時擁有多個角色（如稽核員＋驗證決定委員），
     * 能力判斷取所有角色的並集。至少一個。
     * 接受任意非空字串，支援動態自定義角色代碼（後端校驗代碼存在於 roles 表）。
     */
    roles: z.array(z.string().min(1)).min(1),
    /**
     * 主角色（選單／工作台分發用）。省略時由後端取 roles[0]。
     * 若指定，後端會 normalize 確保其落在 roles 之內。
     */
    primaryRole: z.string().min(1).optional(),
    initialPassword: adminAssignedPasswordSchema.default(DEFAULT_INITIAL_PASSWORD),
});
/**
 * 更新員工帳號請求 schema（所有欄位可選）。
 * 密碼重設由獨立的 POST /:id/reset-password 端點處理。
 */
export const updateStaffSchema = z.object({
    /**
     * 登入帳號（username）。序 52：系統管理員可修改員工登入帳號。
     * 提供時後端會做唯一性預檢（排除自身、排除已軟刪除），衝突回 409 DUPLICATE_USERNAME。
     * 僅允許英數與底線／連字號／點，長度 3-50，避免奇異字元造成登入問題。
     */
    username: z
        .string()
        .min(3)
        .max(50)
        .regex(/^[A-Za-z0-9._-]+$/, '登入帳號僅能包含英文、數字與 . _ - 符號')
        .optional(),
    name: z.string().min(1).max(100).optional(),
    email: z.string().email().optional(),
    // phone 在 DB 為可空欄位，前端清空時會送 null，故用 nullish 同時允許 null 與 undefined
    phone: z.string().max(20).nullish(),
    /**
     * 角色能力清單（多角色）；提供時至少一個。
     * 接受任意非空字串，支援動態自定義角色代碼（後端校驗代碼存在於 roles 表）。
     */
    roles: z.array(z.string().min(1)).min(1).optional(),
    /** 主角色（選單／工作台分發用）；省略時後端沿用既有或取 roles[0] */
    primaryRole: z.string().min(1).optional(),
    isActive: z.boolean().optional(),
});
